/**
 * OPL Overview page - ECharts pie chart for issue status distribution.
 */

import { useEffect, useRef, useState } from 'react';
import { Card, Empty, Spin, Typography } from 'antd';
import ReactECharts from 'echarts-for-react';
import { useOplStore } from '@/store/oplStore';

const { Text, Title } = Typography;

interface OplOverviewProps {
  projectId: number;
  onNavigateToIssue?: (issueId: number) => void;
}

const OplOverview: React.FC<OplOverviewProps> = ({
  projectId,
  onNavigateToIssue,
}) => {
  const { overview, isLoading, fetchOverview } = useOplStore();
  const [chartReady, setChartReady] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    // Track component mount state
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchOverview(projectId);
  }, [projectId, fetchOverview]);

  // Delay chart rendering to ensure DOM is ready
  // This fixes the "Can't get DOM width or height" ECharts warning
  useEffect(() => {
    if (!overview || overview.total_count === 0) {
      setChartReady(false);
      return;
    }

    // Reset chartReady first
    setChartReady(false);

    // Use double requestAnimationFrame to wait for browser to complete layout
    // This is more reliable than setTimeout for DOM-dependent operations
    let rafId1: number;
    let rafId2: number;

    rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(() => {
        if (isMountedRef.current) {
          setChartReady(true);
        }
      });
    });

    return () => {
      cancelAnimationFrame(rafId1);
      cancelAnimationFrame(rafId2);
    };
  }, [overview]);

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <Spin size="large" />
      </div>
    );
  }

  if (!overview || overview.total_count === 0) {
    return <Empty description="暂无问题数据" />;
  }

  const chartOption = {
    tooltip: {
      trigger: 'item' as const,
      formatter: '{b}: {c} ({d}%)',
    },
    legend: {
      orient: 'horizontal' as const,
      bottom: 0,
    },
    series: [
      {
        name: '问题状态',
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 6,
          borderColor: '#fff',
          borderWidth: 2,
        },
        label: {
          show: true,
          formatter: '{b}: {c}',
        },
        data: [
          {
            value: overview.opened_count,
            name: '开放',
            itemStyle: { color: '#52c41a' },
          },
          {
            value: overview.closed_count,
            name: '已关闭',
            itemStyle: { color: '#d9d9d9' },
          },
        ],
      },
    ],
  };

  return (
    <div>
      <Card className="mb-4">
        <Title level={5}>问题状态分布</Title>
        {!chartReady ? (
          <div className="flex items-center justify-center" style={{ height: 300, width: '100%' }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            <div className="flex justify-center" style={{ height: 300, width: '100%' }}>
              <ReactECharts
                key={`chart-${overview.total_count}-${overview.opened_count}`}
                option={chartOption}
                style={{ height: '100%', width: '100%', maxWidth: 500 }}
                opts={{ renderer: 'svg' }}
                notMerge
                lazyUpdate
              />
            </div>
            <div className="text-center mt-2">
              <Text type="secondary">
                共 {overview.total_count} 条问题 | 开放 {overview.opened_count} | 已关闭{' '}
                {overview.closed_count}
              </Text>
            </div>
          </>
        )}
      </Card>

      <div className="grid grid-cols-2 gap-4">
        <Card
          title={<Text style={{ color: '#52c41a' }}>开放问题 ({overview.opened_count})</Text>}
          size="small"
        >
          {overview.opened_issues.length === 0 ? (
            <Text type="secondary">无</Text>
          ) : (
            <div>
              {overview.opened_issues.map((item) => (
                <div
                  key={item.id}
                  className="cursor-pointer hover:bg-gray-50 py-2 border-b border-gray-100 last:border-0"
                  onClick={() => onNavigateToIssue?.(item.id)}
                >
                  <Text className="text-blue-600">{item.issue_id}</Text>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card
          title={<Text type="secondary">已关闭问题 ({overview.closed_count})</Text>}
          size="small"
        >
          {overview.closed_issues.length === 0 ? (
            <Text type="secondary">无</Text>
          ) : (
            <div>
              {overview.closed_issues.map((item) => (
                <div
                  key={item.id}
                  className="cursor-pointer hover:bg-gray-50 py-2 border-b border-gray-100 last:border-0"
                  onClick={() => onNavigateToIssue?.(item.id)}
                >
                  <Text>{item.issue_id}</Text>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default OplOverview;
